import React, { useState } from 'react';
import {
  Plus,
  Search,
  LayoutGrid,
  RefreshCw,
  Loader2,
  List,
  Table,
  Users
} from 'lucide-react';
import KanbanColumn from './components/kanban/KanbanColumn';
import TaskModal from './components/kanban/TaskModal';
import CreateTaskModal from './components/kanban/CreateTaskModal';
import TaskHistoryModal from './components/history/TaskHistoryModal';
import ConfirmDialog from './components/shared/ConfirmDialog';
import ListView from './components/board/ListView';
import TableView from './components/board/TableView';
import { mockTasks, mockMembers, mockTeams, mockHistory } from './components/data/mockData';


const COLUMNS = ['backlog', 'todo', 'in_progress', 'review', 'done'];

export default function Board() {
  const [selectedTask, setSelectedTask] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createInitialStatus, setCreateInitialStatus] = useState('backlog');
  const [searchQuery, setSearchQuery] = useState('');
  const [historyTask, setHistoryTask] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban', 'list', 'table'
  const [isLoading, setIsLoading] = useState(false);

  const [tasks, setTasks] = useState(mockTasks);
  const [members] = useState(mockMembers);
  const [teams] = useState(mockTeams);
  const [history, setHistory] = useState(mockHistory);

  // Get teams where current user is a member (for now, show all teams)
  const userTeams = teams.filter((t) => t.active);

  const refetch = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 500);
  };

  const handleAddTask = (status) => {
    setCreateInitialStatus(status);
    setIsCreateModalOpen(true);
  };

  const handleCreateTask = (taskData) => {
    const tasksInColumn = tasks.filter((t) => t.status === taskData.status);
    const newTask = {
      ...taskData,
      id: `task${Date.now()}`,
      order: tasksInColumn.length,
      team_id: selectedTeamId || (userTeams.length > 0 ? userTeams[0].id : null),
      created_date: new Date().toISOString(),
      created_by: 'Usuário'
    };

    setTasks([...tasks, newTask]);

    // Log history
    const newHistoryEntry = {
      id: `h${Date.now()}`,
      task_id: newTask.id,
      task_title: newTask.title,
      action: 'created',
      field_changed: null,
      old_value: null,
      new_value: null,
      changed_by: 'Usuário',
      created_date: new Date().toISOString()
    };
    setHistory([newHistoryEntry, ...history]);
  };

  const handleUpdateTask = (taskData) => {
    const oldTask = tasks.find((t) => t.id === taskData.id);
    setTasks(tasks.map((t) => (t.id === taskData.id ? taskData : t)));

    // Log history for status change
    if (oldTask?.status !== taskData.status) {
      const newHistoryEntry = {
        id: `h${Date.now()}`,
        task_id: taskData.id,
        task_title: taskData.title,
        action: 'status_changed',
        field_changed: 'status',
        old_value: oldTask?.status,
        new_value: taskData.status,
        changed_by: 'Usuário',
        created_date: new Date().toISOString()
      };
      setHistory([newHistoryEntry, ...history]);
    }

    setSelectedTask(null);
  };

  const handleDeleteTask = (task: any) => {
    setDeleteConfirm({ id: task.id, title: task.title });
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      setTasks(tasks.filter((t) => t.id !== deleteConfirm.id));

      // Log history
      const newHistoryEntry = {
        id: `h${Date.now()}`,
        task_id: deleteConfirm.id,
        task_title: deleteConfirm.title,
        action: 'deleted',
        field_changed: null,
        old_value: null,
        new_value: null,
        changed_by: 'Usuário',
        created_date: new Date().toISOString()
      };
      setHistory([newHistoryEntry, ...history]);

      setSelectedTask(null);
    }
  };

  // Filter tasks by search query and selected team
  const filteredTasks = tasks.filter((task: any) => {
    // Filter by team if a specific team is selected
    if (selectedTeamId && task.team_id !== selectedTeamId) return false;

    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      task.title?.toLowerCase().includes(query) ||
      task.description?.toLowerCase().includes(query) ||
      task.assignee?.toLowerCase().includes(query) ||
      task.tags?.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  // If no team selected (all teams), group by team first
  const tasksByTeam = selectedTeamId
    ? null
    : userTeams.reduce((acc, team) => {
        acc[team.id] = filteredTasks.filter((task) => task.team_id === team.id);
        return acc;
      }, {});

  // Group tasks by status (quando selectedTeamId está setado, tasksByStatus usa filteredTasks do time selecionado)
  const tasksByStatus = COLUMNS.reduce((acc, status) => {
    acc[status] = filteredTasks.filter((task) => task.status === status);
    return acc;
  }, {});

  const totalTasks = filteredTasks.length;
  const completedTasks = filteredTasks.filter((t) => t.status === 'done').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/30">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200/50 sticky top-0 z-10 shadow-sm">
        <div className="px-4 md:px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Logo and Title */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-200 ring-4 ring-blue-100">
                <LayoutGrid className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                  Quadro de Tarefas
                </h1>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <p className="text-sm text-slate-600 font-medium">
                      {completedTasks} de {totalTasks} concluídas
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {/* Team Filter */}
              <select
                value={selectedTeamId || 'all'}
                onChange={(e) => setSelectedTeamId(e.target.value === 'all' ? null : e.target.value)}
                className="h-10 px-4 rounded-lg border-2 border-slate-200 bg-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">📊 Todas as Equipes</option>
                {userTeams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>

              {/* Search */}
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar tarefas..."
                  className="w-full pl-9 pr-4 py-2 bg-white/80 border-2 border-slate-200 rounded-lg shadow-sm focus:shadow-md focus:border-blue-300 focus:outline-none transition-all"
                />
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 bg-white/80 rounded-lg p-1 border border-slate-200 shadow-sm">
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'kanban' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Table className="w-4 h-4" />
                </button>
              </div>

              {/* Refresh */}
              <button
                onClick={() => refetch()}
                disabled={isLoading}
                className="p-2 shrink-0 border-2 border-slate-200 bg-white rounded-lg hover:scale-105 transition-transform shadow-sm hover:shadow-md disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>

              {/* New Task */}
              <button
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg shrink-0 shadow-lg shadow-blue-200 hover:shadow-xl hover:scale-105 transition-all font-semibold flex items-center gap-1"
                onClick={() => handleAddTask('backlog')}
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Nova Tarefa</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Board */}
      <main className="p-4 md:p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : viewMode === 'kanban' ? (
          selectedTeamId ? (
            // Single team board (sem DragDropContext)
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory md:snap-none">
              {COLUMNS.map((status) => (
                <div key={status} className="snap-center">
                  <KanbanColumn
                    status={status}
                    tasks={tasksByStatus[status]}
                    onTaskClick={setSelectedTask}
                    onAddTask={handleAddTask}
                    onHistoryClick={setHistoryTask}
                    members={members}
                  />
                </div>
              ))}
            </div>
          ) : (
            // All teams - multiple boards (sem DragDropContext)
            <div className="space-y-8">
              {userTeams.map((team) => {
                const teamTasks = tasksByTeam?.[team.id] || [];
                const teamTasksByStatus = COLUMNS.reduce((acc, status) => {
                  acc[status] = teamTasks.filter((task) => task.status === status);
                  return acc;
                }, {});

                const teamColor =
                  {
                    blue: 'from-blue-600 to-cyan-600',
                    purple: 'from-purple-600 to-pink-600',
                    green: 'from-emerald-600 to-teal-600',
                    orange: 'from-orange-600 to-amber-600',
                    red: 'from-red-600 to-rose-600',
                    indigo: 'from-indigo-600 to-blue-600'
                  }[team.color] || 'from-blue-600 to-cyan-600';

                return (
                  <div key={team.id} className="bg-white/40 rounded-2xl p-4 border-2 border-slate-200/50">
                    {/* Team Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${teamColor} flex items-center justify-center shadow-lg`}>
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-slate-800">{team.name}</h2>
                          <p className="text-xs text-slate-600">{teamTasks.length} tarefas</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedTeamId(team.id)}
                        className="px-3 py-1.5 text-sm border-2 border-slate-200 bg-white rounded-md hover:bg-blue-50 transition-colors"
                      >
                        Ver detalhes
                      </button>
                    </div>

                    {/* Team Board */}
                    <div className="flex gap-4 overflow-x-auto pb-2">
                      {COLUMNS.map((status) => (
                        <div key={status} className="flex-shrink-0">
                          <KanbanColumn
                            status={status}
                            tasks={teamTasksByStatus[status]}
                            onTaskClick={setSelectedTask}
                            onAddTask={handleAddTask}
                            onHistoryClick={setHistoryTask}
                            members={members}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : viewMode === 'list' ? (
          <ListView tasks={filteredTasks} members={members} onTaskClick={setSelectedTask} onHistoryClick={setHistoryTask} />
        ) : (
          <TableView tasks={filteredTasks} members={members} onTaskClick={setSelectedTask} onHistoryClick={setHistoryTask} />
        )}
      </main>

      {/* Task Detail Modal */}
      <TaskModal
        task={selectedTask}
        open={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        onUpdate={handleUpdateTask}
        onDelete={handleDeleteTask}
        members={members}
      />

      {/* Create Task Modal */}
      <CreateTaskModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateTask}
        initialStatus={createInitialStatus}
        members={members}
      />

      {/* Task History Modal */}
      <TaskHistoryModal
        taskId={historyTask?.id}
        taskTitle={historyTask?.title}
        open={!!historyTask}
        onClose={() => setHistoryTask(null)}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title="Excluir Tarefa"
        description="Tem certeza que deseja excluir esta tarefa? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        variant="danger"
      />
    </div>
  );
}
